import { captureError } from '@smg-real-estate/browser-rum';
import type { Ref } from 'vue';
import type { VueI18n } from 'vue-i18n';

import * as ToplistingApiService from '@/api/toplistingService';
import { runBackoffPoll } from '@/helpers/polling/runBackoffPoll';
import { getRegionalHighlightStatusForListing } from '@/helpers/regionalHighlightListingStatusLookup';
import { peekRegionalHighlightSlotsForListing } from '@/helpers/regionalHighlightPeek';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useAppStore } from '@/stores/app/useAppStore';
import { useInsertionsStore } from '@/stores/insertions/useInsertionsStore';
import { createRegionalHighlightPurchaseActions } from '@/stores/toplisting/regionalHighlightPurchase';
import { resolveInsertionOwnerId } from '@/stores/toplisting/resolveInsertionOwnerId';
import { ToastTheme } from '@/stores/types/app';
import { ApiCallStatus } from '@/stores/types/insertions';
import { InsertionStatus, type Insertion } from '@/types';
import type {
  RegionalHighlightAssignmentRequest,
  RegionalHighlightAssignmentResponse,
  RegionalHighlightCallbackRequest,
  RegionalHighlightCallbackResponse,
  RegionalHighlightDeactivationRequest,
  RegionalHighlightDraftAddress,
  RegionalHighlightListingStatus,
  RegionalHighlightListingStatusesByListingId,
  RegionalHighlightPurchaseOptionsResponse,
  RegionalHighlightPurchaseOrder,
  RegionalHighlightPurchaseOrdersResponse,
  RegionalHighlightSlot,
  RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';

export type RegionalHighlightOperation =
  | 'slots'
  | 'listingStatuses'
  | 'assign'
  | 'deactivate'
  | 'callback'
  | 'purchaseOptions'
  | 'purchaseOrders'
  | 'createPurchaseOrder'
  | 'extendSlot'
  | 'autoRenew';

export interface RegionalHighlightApiStatus {
  slots: ApiCallStatus;
  listingStatuses: ApiCallStatus;
  assign: ApiCallStatus;
  deactivate: ApiCallStatus;
  callback: ApiCallStatus;
  purchaseOptions: ApiCallStatus;
  purchaseOrders: ApiCallStatus;
  createPurchaseOrder: ApiCallStatus;
  extendSlot: ApiCallStatus;
  autoRenew: ApiCallStatus;
}

export type RegionalHighlightErrors = Partial<Record<RegionalHighlightOperation, unknown>>;

/**
 * Poll cadence for a deferred Regional Highlight activation, ~58s in total.
 *
 * Deliberately a standalone budget rather than one derived from the insertion
 * processing poll: a Regional Highlight booking additionally waits for the importer
 * `LISTING_PUBLISHED` event to complete the pending assignment after the insertion
 * itself reaches `Published`. Provisional, to be tuned from telemetry.
 */
export const REGIONAL_HIGHLIGHT_POLL_DELAYS_MS = [
  2_000, 3_000, 5_000, 8_000, 10_000, 10_000, 10_000, 10_000,
];

export interface RegionalHighlightRefs {
  regionalHighlightSlots: Ref<RegionalHighlightSlot[] | undefined>;
  regionalHighlightSlotsResponse: Ref<RegionalHighlightSlotsResponse | undefined>;
  regionalHighlightListingStatuses: Ref<RegionalHighlightListingStatusesByListingId | undefined>;
  regionalHighlightApiStatus: Ref<RegionalHighlightApiStatus>;
  regionalHighlightErrors: Ref<RegionalHighlightErrors>;
  regionalHighlightPurchaseOptions: Ref<RegionalHighlightPurchaseOptionsResponse | undefined>;
  regionalHighlightPurchaseOrders: Ref<RegionalHighlightPurchaseOrdersResponse | undefined>;
  regionalHighlightLastPurchaseOrder: Ref<RegionalHighlightPurchaseOrder | undefined>;
  regionalHighlightLastAssignment: Ref<RegionalHighlightAssignmentResponse | undefined>;
  regionalHighlightLastCallback: Ref<RegionalHighlightCallbackResponse | undefined>;
  insertionIdsWithPendingRegionalHighlight: Ref<string[]>;
}

export const getInitialRegionalHighlightApiStatus = (): RegionalHighlightApiStatus => ({
  slots: ApiCallStatus.READY,
  listingStatuses: ApiCallStatus.READY,
  assign: ApiCallStatus.READY,
  deactivate: ApiCallStatus.READY,
  callback: ApiCallStatus.READY,
  purchaseOptions: ApiCallStatus.READY,
  purchaseOrders: ApiCallStatus.READY,
  createPurchaseOrder: ApiCallStatus.READY,
  extendSlot: ApiCallStatus.READY,
  autoRenew: ApiCallStatus.READY,
});

export const setRegionalHighlightApiStatus = (
  state: RegionalHighlightRefs,
  payload: { operation: RegionalHighlightOperation; status: ApiCallStatus },
): void => {
  state.regionalHighlightApiStatus.value = {
    ...state.regionalHighlightApiStatus.value,
    [payload.operation]: payload.status,
  };
};

const clearRegionalHighlightError = (
  state: RegionalHighlightRefs,
  operation: RegionalHighlightOperation,
): void => {
  const { [operation]: _removed, ...remainingErrors } = state.regionalHighlightErrors.value;
  state.regionalHighlightErrors.value = remainingErrors;
};

const clearRegionalHighlightAssignError = (state: RegionalHighlightRefs): void => {
  clearRegionalHighlightError(state, 'assign');
};

const setRegionalHighlightError = (
  state: RegionalHighlightRefs,
  payload: { operation: RegionalHighlightOperation; error: unknown },
): void => {
  state.regionalHighlightErrors.value = {
    ...state.regionalHighlightErrors.value,
    [payload.operation]: payload.error,
  };
};

const setRegionalHighlightSlotsResponse = (
  state: RegionalHighlightRefs,
  response: RegionalHighlightSlotsResponse,
): void => {
  state.regionalHighlightSlotsResponse.value = response;
  state.regionalHighlightSlots.value = response.slots;
};

const clearRegionalHighlightSlotsResponse = (state: RegionalHighlightRefs): void => {
  state.regionalHighlightSlotsResponse.value = undefined;
  state.regionalHighlightSlots.value = undefined;
};

const setRegionalHighlightListingStatuses = (
  state: RegionalHighlightRefs,
  statuses: RegionalHighlightListingStatusesByListingId,
): void => {
  state.regionalHighlightListingStatuses.value = statuses;
};

const mergeRegionalHighlightListingStatuses = (
  state: RegionalHighlightRefs,
  statuses: RegionalHighlightListingStatusesByListingId,
): void => {
  state.regionalHighlightListingStatuses.value = {
    ...(state.regionalHighlightListingStatuses.value ?? {}),
    ...statuses,
  };
};

const isActiveRegionalHighlightAgency = (capturedAgencyId: string): boolean =>
  useAgencyStore().activeAgencyId === capturedAgencyId;

const discardStaleRegionalHighlightOperation = (
  state: RegionalHighlightRefs,
  operation: RegionalHighlightOperation,
): false => {
  setRegionalHighlightApiStatus(state, { operation, status: ApiCallStatus.READY });
  return false;
};

export const runRegionalHighlightOperation = async (
  state: RegionalHighlightRefs,
  operation: RegionalHighlightOperation,
  capturedAgencyId: string,
  action: () => Promise<(() => void) | undefined>,
): Promise<boolean> => {
  if (!isActiveRegionalHighlightAgency(capturedAgencyId)) {
    return false;
  }

  setRegionalHighlightApiStatus(state, {
    operation,
    status: ApiCallStatus.IN_PROGRESS,
  });
  clearRegionalHighlightError(state, operation);

  try {
    const commitResult = await action();
    if (!isActiveRegionalHighlightAgency(capturedAgencyId)) {
      return discardStaleRegionalHighlightOperation(state, operation);
    }
    commitResult?.();
    setRegionalHighlightApiStatus(state, {
      operation,
      status: ApiCallStatus.SUCCESS,
    });
    return true;
  } catch (error) {
    if (!isActiveRegionalHighlightAgency(capturedAgencyId)) {
      return discardStaleRegionalHighlightOperation(state, operation);
    }
    setRegionalHighlightApiStatus(state, {
      operation,
      status: ApiCallStatus.ERROR,
    });
    setRegionalHighlightError(state, { operation, error });
    captureError(error);
    return false;
  }
};

const refreshRegionalHighlightListingState = async (
  state: RegionalHighlightRefs,
  activeAgencyId: string,
  listingId: string,
): Promise<void> => {
  const listingIds = Array.from(
    new Set([...Object.keys(state.regionalHighlightListingStatuses.value ?? {}), listingId]),
  );

  await runRegionalHighlightOperation(state, 'slots', activeAgencyId, async () => {
    const response = await ToplistingApiService.fetchRegionalHighlightSlots(
      activeAgencyId,
      listingId,
    );
    return () => setRegionalHighlightSlotsResponse(state, response);
  });
  await runRegionalHighlightOperation(state, 'listingStatuses', activeAgencyId, async () => {
    const statuses = await ToplistingApiService.fetchRegionalHighlightListingStatuses(
      activeAgencyId,
      { listingIds },
    );
    return () => setRegionalHighlightListingStatuses(state, statuses);
  });
};

export type RegionalHighlightAssignRefreshScope = 'listing' | 'overview';

/**
 * One poll attempt for a deferred activation: the insertion is refetched first,
 * because a wizard draft only gets its listing id once it reaches `Published`, and
 * only then can the listing-keyed Regional Highlight status be read.
 *
 * The status request is deliberately not wrapped in `runRegionalHighlightOperation`:
 * this poll is detached from the page that owns `regionalHighlightApiStatus`, and it
 * must not flip that shared state on every attempt.
 *
 * A failing attempt is reported and treated as "not landed yet", so a single flaky
 * request does not end the poll early.
 */
const hasRegionalHighlightLanded = async (
  state: RegionalHighlightRefs,
  payload: { activeAgencyId: string; userId: string; insertionId: string },
): Promise<boolean> => {
  try {
    const insertion: Insertion | undefined = await useInsertionsStore().refetchInsertionById({
      userId: payload.userId,
      insertionId: payload.insertionId,
    });

    const listingId = insertion?.listing?.id;
    if (insertion?.status !== InsertionStatus.Published || !listingId) {
      return false;
    }

    const statuses = await ToplistingApiService.fetchRegionalHighlightListingStatuses(
      payload.activeAgencyId,
      { listingIds: [listingId] },
    );
    mergeRegionalHighlightListingStatuses(state, statuses);

    return getRegionalHighlightStatusForListing(statuses, listingId)?.hasRegionalHighlight === true;
  } catch (error) {
    captureError(error);
    return false;
  }
};

/**
 * Nudges the user to refresh once the activation budget is spent.
 *
 * The i18n bootstrap is imported lazily: a static import pulls the app-wide message
 * bundle into the module graph of every store consumer and breaks the specs that
 * partially mock `vue-i18n` without `createI18n`.
 */
const showRegionalHighlightPendingTimeoutToast = async (): Promise<void> => {
  const { i18n } = await import('@/i18n-setup-hg');
  const translate = (key: string): string => (i18n.global as VueI18n).t(key).toString();

  useAppStore().showToast({
    title: translate('manageAdsPage.toasts.regionalHighlightPendingTimeout.title'),
    message: translate('manageAdsPage.toasts.regionalHighlightPendingTimeout.message'),
    theme: ToastTheme.Warning,
  });
};

const appendToInsertionIdsWithPendingRegionalHighlight = (
  state: RegionalHighlightRefs,
  insertionId: string,
): void => {
  if (state.insertionIdsWithPendingRegionalHighlight.value.includes(insertionId)) {
    return;
  }

  state.insertionIdsWithPendingRegionalHighlight.value = [
    ...state.insertionIdsWithPendingRegionalHighlight.value,
    insertionId,
  ];
};

const removeFromInsertionIdsWithPendingRegionalHighlight = (
  state: RegionalHighlightRefs,
  insertionId: string,
): void => {
  state.insertionIdsWithPendingRegionalHighlight.value =
    state.insertionIdsWithPendingRegionalHighlight.value.filter((id) => id !== insertionId);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- action bag is returned into the setup store
export const createRegionalHighlightActions = (state: RegionalHighlightRefs) => {
  const getRegionalHighlightListingStatusByListingId = (
    listingId: string,
  ): RegionalHighlightListingStatus | undefined =>
    getRegionalHighlightStatusForListing(state.regionalHighlightListingStatuses.value, listingId);

  const getIsPendingRegionalHighlightByInsertionId = (insertionId: string): boolean =>
    state.insertionIdsWithPendingRegionalHighlight.value.includes(insertionId);

  const fetchRegionalHighlightSlots = async (listingId?: string): Promise<void> => {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      setRegionalHighlightApiStatus(state, { operation: 'slots', status: ApiCallStatus.ERROR });
      return;
    }

    await runRegionalHighlightOperation(state, 'slots', activeAgencyId, async () => {
      const response = await ToplistingApiService.fetchRegionalHighlightSlots(
        activeAgencyId,
        listingId,
      );
      return () => setRegionalHighlightSlotsResponse(state, response);
    });
  };

  const fetchRegionalHighlightSlotsForAddress = async (
    address: RegionalHighlightDraftAddress,
  ): Promise<void> => {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId || !address.postalCode.trim()) {
      return;
    }

    clearRegionalHighlightSlotsResponse(state);
    await runRegionalHighlightOperation(state, 'slots', activeAgencyId, async () => {
      const response = await ToplistingApiService.fetchRegionalHighlightSlotsForAddress(
        activeAgencyId,
        address,
      );
      return () => setRegionalHighlightSlotsResponse(state, response);
    });
  };

  const recordRegionalHighlightPendingAssignment = async (payload: {
    propertyReferenceId: string;
    slotId: string;
  }): Promise<void> => {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      throw new Error('Cannot record Regional Highlight intent without an active agency');
    }

    await ToplistingApiService.recordRegionalHighlightPendingAssignment(
      activeAgencyId,
      payload.propertyReferenceId,
      payload.slotId,
    );
  };

  const forgetRegionalHighlightPendingAssignment = async (
    propertyReferenceId: string,
  ): Promise<void> => {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      return;
    }

    try {
      await ToplistingApiService.forgetRegionalHighlightPendingAssignment(
        activeAgencyId,
        propertyReferenceId,
      );
    } catch (error) {
      captureError(error);
    }
  };

  const fetchRegionalHighlightListingStatuses = async (listingIds: string[]): Promise<void> => {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId || listingIds.length === 0) {
      return;
    }

    await runRegionalHighlightOperation(state, 'listingStatuses', activeAgencyId, async () => {
      const statuses = await ToplistingApiService.fetchRegionalHighlightListingStatuses(
        activeAgencyId,
        { listingIds },
      );
      return () => setRegionalHighlightListingStatuses(state, statuses);
    });
  };

  const refreshOverviewAfterAssign = async (assignedListingId: string): Promise<void> => {
    await fetchRegionalHighlightSlots();
    const listingIds = Array.from(
      new Set([
        ...Object.keys(state.regionalHighlightListingStatuses.value ?? {}),
        ...(state.regionalHighlightSlots.value ?? [])
          .map((slot) => slot.activeBooking?.listingId)
          .filter((id): id is string => Boolean(id)),
        assignedListingId,
      ]),
    );
    if (listingIds.length > 0) {
      await fetchRegionalHighlightListingStatuses(listingIds);
    }
  };

  const assignRegionalHighlightSlot = async (
    payload: {
      slotId: string;
      assignment: RegionalHighlightAssignmentRequest;
    },
    options?: { refreshScope?: RegionalHighlightAssignRefreshScope },
  ): Promise<void> => {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      return;
    }

    const refreshScope = options?.refreshScope ?? 'listing';

    const assignmentSucceeded = await runRegionalHighlightOperation(
      state,
      'assign',
      activeAgencyId,
      async () => {
        const assignment = await ToplistingApiService.assignRegionalHighlightSlot(
          activeAgencyId,
          payload.slotId,
          payload.assignment,
        );
        return () => {
          state.regionalHighlightLastAssignment.value = assignment;
        };
      },
    );
    if (!assignmentSucceeded) {
      return;
    }

    if (refreshScope === 'overview') {
      await refreshOverviewAfterAssign(payload.assignment.listingId);
      return;
    }

    await refreshRegionalHighlightListingState(state, activeAgencyId, payload.assignment.listingId);
  };

  const deactivateRegionalHighlightSlot = async (payload: {
    slotId: string;
    deactivation?: RegionalHighlightDeactivationRequest;
  }): Promise<void> => {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      return;
    }

    const deactivationSucceeded = await runRegionalHighlightOperation(
      state,
      'deactivate',
      activeAgencyId,
      async () => {
        await ToplistingApiService.deactivateRegionalHighlightSlot(
          activeAgencyId,
          payload.slotId,
          payload.deactivation,
        );
        return undefined;
      },
    );
    const listingId = payload.deactivation?.listingId;
    if (deactivationSucceeded && listingId) {
      useInsertionsStore().setListingActionSuccess(
        'manageAdsPage.toasts.regionalHighlightWillBeDeactivated',
      );
      await refreshRegionalHighlightListingState(state, activeAgencyId, listingId);
    }
  };

  const requestRegionalHighlightCallback = async (
    request: RegionalHighlightCallbackRequest,
  ): Promise<void> => {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      return;
    }

    await runRegionalHighlightOperation(state, 'callback', activeAgencyId, async () => {
      const callback = await ToplistingApiService.requestRegionalHighlightCallback(
        activeAgencyId,
        request,
      );
      return () => {
        state.regionalHighlightLastCallback.value = callback;
      };
    });
  };

  const clearRegionalHighlightAgencyScopedData = (): void => {
    clearRegionalHighlightSlotsResponse(state);
    state.regionalHighlightPurchaseOptions.value = undefined;
    state.regionalHighlightPurchaseOrders.value = undefined;
    state.regionalHighlightLastPurchaseOrder.value = undefined;
  };

  const purchase = createRegionalHighlightPurchaseActions(state, fetchRegionalHighlightSlots);

  /**
   * Follows a wizard publish until its deferred Regional Highlight booking exists.
   *
   * The booking is completed by `toplisting-service` only once the importer
   * `LISTING_PUBLISHED` event arrives, which is after the one-shot Manage Ads status
   * fetch, so without this the badge would need a manual reload. It lives in the
   * store to survive the navigation away from the wizard, and is meant to be
   * called without awaiting.
   *
   * The pending marker is owned here rather than by the view, and cleared on every
   * exit path, so a pending badge cannot be stranded when the poll never starts.
   *
   * The backend exposes no pending or failed state, only `hasRegionalHighlight`, so
   * an exhausted budget is not proof of failure: the marker is dropped and the user
   * is nudged to refresh instead of being told the booking failed.
   */
  const trackRegionalHighlightAfterPublish = async (payload: {
    insertionId: string;
  }): Promise<void> => {
    const { insertionId } = payload;

    if (state.insertionIdsWithPendingRegionalHighlight.value.includes(insertionId)) {
      return;
    }

    const activeAgencyId = useAgencyStore().activeAgencyId;
    const userId = resolveInsertionOwnerId(insertionId);

    if (!activeAgencyId || !userId) {
      return;
    }

    appendToInsertionIdsWithPendingRegionalHighlight(state, insertionId);

    const confirmed = await runBackoffPoll(REGIONAL_HIGHLIGHT_POLL_DELAYS_MS, () =>
      hasRegionalHighlightLanded(state, {
        activeAgencyId,
        userId,
        insertionId,
      }),
    );

    removeFromInsertionIdsWithPendingRegionalHighlight(state, insertionId);

    if (!confirmed) {
      await showRegionalHighlightPendingTimeoutToast();
    }
  };

  return {
    getRegionalHighlightListingStatusByListingId,
    getIsPendingRegionalHighlightByInsertionId,
    fetchRegionalHighlightSlots,
    fetchRegionalHighlightSlotsForAddress,
    recordRegionalHighlightPendingAssignment,
    forgetRegionalHighlightPendingAssignment,
    fetchRegionalHighlightListingStatuses,
    assignRegionalHighlightSlot,
    peekRegionalHighlightSlotsForListing,
    clearRegionalHighlightAssignError: () => clearRegionalHighlightAssignError(state),
    deactivateRegionalHighlightSlot,
    requestRegionalHighlightCallback,
    clearRegionalHighlightAgencyScopedData,
    ...purchase,
    trackRegionalHighlightAfterPublish,
  };
};
